export interface LoginCredentials {
  username: string;
  password: string;
}

export interface AuthTokens {
  access: string;
  refresh: string;
}

export interface AuthResponse {
  access: string;
  refresh: string;
  user: UserInfo;
}

export interface UserInfo {
  id: number;
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  app_user?: AppUserInfo;
}

export type LanguageCode =
  | "es"
  | "en"
  | "it"
  | "pt"
  | "es-PE"
  | "es-CL"
  | "es-UY";

export interface AppUserInfo {
  id: number;
  name: string;
  user_type: number;
  user_type_display: string;
  portfolio: number;
  phone_number: string;
  email: string;
  code: string;
  /** Absolute (or storage-relative) URL for the avatar image, or null. */
  avatar_url: string | null;
  /** Identifier of a preset avatar (e.g. "avatar-3"); "" when unset. */
  avatar_choice: string;
  /** User-set preferred locale; "" when unset. */
  language: LanguageCode | "";
  /** Locale resolved by the backend (user preference, falling back to portfolio). */
  effective_language: LanguageCode;
  /** Audit company ID — only meaningful for user_type 4 and 5. */
  audit_company: number | null;
  audit_company_name: string | null;
  /** Portfolio codes a user_type 4 (Auditor Manager) is allowed to supervise. */
  allowed_portfolios: number[];
}

/** Auditor user types (Manager + Auditor). Used for sider/route gating. */
export const AUDITOR_USER_TYPES = [4, 5] as const;

/** Admin user types (CYBER + ESA). Used for admin routes. */
export const ADMIN_USER_TYPES = [1, 2] as const;

/** Cyber Admin (equipo Cyberagropolis) — acceso global sin restricción. */
export const CYBER_ADMIN_USER_TYPE = 1;

/** ESA Admin (Eurosemillas) — cuando trae `portfolio` asignado queda
 *  scoped a los datos de ese país. Sin portfolio → sin acceso. */
export const ESA_ADMIN_USER_TYPE = 2;

/**
 * `true` cuando el usuario es un ESA Admin con portfolio asignado —
 * es decir, un perfil Eurosemillas restringido a un país concreto.
 * El backend ya lo scopea vía `apps/shared/esa_scope.py`; el frontend
 * usa este check para pintar el menú simplificado y ocultar acciones
 * globales que este usuario no puede ejecutar.
 */
export const isEurosemillasScoped = (
  identity: UserInfo | undefined | null,
): boolean => {
  const au = identity?.app_user;
  if (!au) return false;
  return au.user_type === ESA_ADMIN_USER_TYPE && au.portfolio > 0;
};

/**
 * `true` para el Cyber Admin (user_type=1) — acceso admin global.
 * Se separa de `ADMIN_USER_TYPES` para poder distinguirlo del ESA
 * Admin scoped en los sitios que necesitan gate a Cyber-only
 * (p. ej. administración de usuarios y catálogos).
 */
export const isCyberAdmin = (
  identity: UserInfo | undefined | null,
): boolean =>
  identity?.app_user?.user_type === CYBER_ADMIN_USER_TYPE;

/**
 * `true` cuando el usuario puede **editar/crear/borrar** datos
 * (auditorías, fincas, productores, plot_reals, samples…). Los ESA
 * Admin scoped son solo-lectura por política: pueden ver y descargar
 * pero no modificar. Cyber Admin y el resto de roles admin sí pueden
 * (aunque en la práctica el ADMIN_USER_TYPES no incluye a auditores/
 * coordinadores; para ellos se aplican otras reglas de rol en cada
 * viewset).
 */
export const canWriteData = (
  identity: UserInfo | undefined | null,
): boolean => {
  if (!identity?.app_user) return false;
  if (isEurosemillasScoped(identity)) return false;
  return (ADMIN_USER_TYPES as readonly number[]).includes(
    identity.app_user.user_type,
  );
};
